/**
 * Google Banana 图片生成节点 - 支持文生图和图生图
 * 轻量级快速生图模型，极速生成，低成本高效率
 */
import React, { memo, useState, useEffect, useCallback } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import './GoogleBananaNode.css';

const apiService = new ApiService();

export const GoogleBananaNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const { readOnly } = useProject();
  
  // 基础设置
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || '1:1');
  const [outputFormat, setOutputFormat] = useState(data.outputFormat || 'png');

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  // 同步节点数据
  useEffect(() => {
    updateNodeData(id, {
      aspectRatio,
      outputFormat,
    });
  }, [id, aspectRatio, outputFormat, updateNodeData]);

  // 获取连接的提示词
  const getConnectedPrompt = useCallback(() => {
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'prompt'
    );
    
    if (connectedEdges.length === 0) return null;
    
    const promptTexts = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      if (sourceNode?.type === 'promptInput' && sourceNode.data?.prompt) {
        promptTexts.push(sourceNode.data.prompt);
      }
    });
    
    return promptTexts.length > 0 ? promptTexts.join('\n') : null;
  }, [edges, getNode, id]);

  // 获取连接的图片（图生图模式）
  const getConnectedImages = useCallback(() => {
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'image'
    );
    
    const images = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      
      // 从图片参考节点获取
      if (sourceNode?.type === 'imageReference' && sourceNode.data?.imageUrl) {
        images.push(sourceNode.data.imageUrl);
      }
      // 从手绘节点获取（只使用已上传到云端的图片）
      else if (sourceNode?.type === 'drawing' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        // 只使用已上传到云端的图片，过滤掉本地 base64 预览
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          images.push(url);
        }
      }
      // 从任务节点获取完成的图片
      else if (sourceNode?.type === 'imageTask' && sourceNode.data?.status === 'completed') {
        const generatedImages = sourceNode.data.generatedImages || [];
        generatedImages.forEach(img => {
          const url = typeof img === 'string' ? img : img.url;
          if (url) images.push(url);
        });
      }
      // 从图片展示节点获取
      else if (sourceNode?.type === 'imageDisplay' && sourceNode.data?.imageUrl) {
        images.push(sourceNode.data.imageUrl);
      }
    });
    
    return images;
  }, [edges, getNode, id]);

  // 验证连接
  const isValidConnection = useCallback((connection) => {
    // 如果连接到文本输入handle，只允许文本输入节点
    if (connection.targetHandle === 'prompt') {
      const sourceNode = getNode(connection.source);
      return sourceNode?.type === 'promptInput';
    }
    
    // 如果连接到图片输入handle
    if (connection.targetHandle === 'image') {
      const sourceNode = getNode(connection.source);
      const validTypes = ['imageReference', 'drawing', 'imageTask', 'imageDisplay'];
      
      if (!validTypes.includes(sourceNode?.type)) {
        return false;
      }
      
      // 限制图片数量（最多10张）
      const currentEdges = getEdges();
      const imageEdgesCount = currentEdges.filter(
        edge => edge.target === id && edge.targetHandle === 'image'
      ).length;
      
      if (imageEdgesCount >= 10) {
        toast.warning('最多支持10张参考图');
        return false;
      }
      
      return true;
    }
    
    return true;
  }, [id, getNode, getEdges]);

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleHandleLabelClick = useCallback((e, handleId, handleType) => {
    e.stopPropagation();  // 阻止事件冒泡
    
    // 只读模式下禁用
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    // 调用统一的创建方法（会自动从配置读取限制）
    createNodeAndConnect(id, 'googleBanana', handleId, handleType);
  }, [id, readOnly, createNodeAndConnect]);

  // 生成图片
  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);

      // 1. 获取提示词
      const prompt = getConnectedPrompt();
      if (!prompt) {
        throw new Error('请先连接文本输入节点');
      }

      // 2. 自动检测模式：根据是否有图片连接判断
      const images = getConnectedImages();
      const isImageToImage = images.length > 0;

      // 3. 获取用户 token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        throw new Error('请先登录');
      }

      // 4. 根据是否有图片选择不同的 app_name
      const appName = isImageToImage ? 'fal-ai/nano-banana/edit' : 'fal-ai/nano-banana';

      // 5. 构建请求参数
      const inputParams = {
        prompt: prompt,
        aspect_ratio: aspectRatio,
        output_format: outputFormat,
      };

      // 图生图模式：添加参考图
      if (isImageToImage) {
        if (images.length === 0) {
          throw new Error('图生图模式需要连接参考图片节点（1-10张）');
        }

        inputParams.image_urls = images;
      }

      // 6. 调用 API 创建任务
      const response = await apiService.request(
        `${apiService.apiBase}/fal/tasks/create`,
        'POST',
        {
          token: userData.token,
          user_type: 1,
          app_name: appName,
          input_params: inputParams,
        }
      );

      if (response.code === 200 && response.data) {
        const taskId = response.data.task_id;
        toast.success(`Google Banana 图片任务已创建！${isImageToImage ? `使用 ${images.length} 张参考图` : ''}`);

        // 7. 创建任务节点
        const currentNode = getNode(id);
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'imageTask',
          position: {
            x: currentNode.position.x + 450,
            y: currentNode.position.y,
          },
          data: {
            taskId: taskId,
            taskType: 'google-banana',
            status: 'pending',
            inputParams: inputParams,
            generatedImages: [],
          },
        };

        // 8. 创建连接边
        const newEdge = {
          id: nanoid(),
          source: id,
          target: taskNodeId,
          type: 'default',
        };

        // 9. 添加节点和边
        await addNodesWithEdges([taskNode], [newEdge]);

        setIsGenerating(false);
      } else {
        throw new Error(response.message || '创建任务失败');
      }

    } catch (err) {
      console.error('Google Banana 图片生成失败:', err);
      const errorMessage = err?.message || '生成失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  // 确定当前模式（自动判断）
  const images = getConnectedImages();
  const isImageToImageMode = images.length > 0;

  return (
    <div className="GoogleBananaNode-container">
      {/* 复制按钮 */}
      <button
        className="GoogleBananaNode-duplicate-button"
        onClick={handleDuplicate}
        title="复制节点"
      >
        <i className="bi bi-copy"></i>
      </button>

      {/* 文本输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="prompt"
        className="GoogleBananaNode-handle GoogleBananaNode-handle-prompt"
        style={{ top: '30%' }}
        isValidConnection={isValidConnection}
      >
        <div
          className="GoogleBananaNode-handle-label GoogleBananaNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'prompt', 'text')}
          title="点击自动创建文本输入节点"
        >
          文本
        </div>
      </Handle>

      {/* 图片输入 Handle（始终显示） */}
      <Handle
        type="target"
        position={Position.Left}
        id="image"
        className="GoogleBananaNode-handle GoogleBananaNode-handle-image"
        style={{ top: '70%' }}
        isValidConnection={isValidConnection}
      >
        <div
          className="GoogleBananaNode-handle-label GoogleBananaNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'image', 'image')}
          title="点击自动创建图片参考节点（最多10张）"
        >
          参考图
        </div>
      </Handle>

      {/* 输出 Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="GoogleBananaNode-handle GoogleBananaNode-handle-output"
      />

      {/* 标题栏 */}
      <div className="GoogleBananaNode-title">
        🍌 谷歌nano banana
        <span className={`GoogleBananaNode-mode-badge ${isImageToImageMode ? 'GoogleBananaNode-mode-i2i' : 'GoogleBananaNode-mode-t2i'}`}>
          {isImageToImageMode ? '图生图' : '文生图'}
        </span>
      </div>

      <div className="GoogleBananaNode-wrapper">
        {/* 连接状态显示 */}
        <div className="GoogleBananaNode-status">
          <div className="GoogleBananaNode-status-item">
            <span className="GoogleBananaNode-status-label">文本输入：</span>
            <span className={`GoogleBananaNode-status-value ${getConnectedPrompt() ? 'GoogleBananaNode-status-connected' : 'GoogleBananaNode-status-disconnected'}`}>
              {getConnectedPrompt() ? '✓ 已连接' : '✗ 未连接'}
            </span>
          </div>
          <div className="GoogleBananaNode-status-item">
            <span className="GoogleBananaNode-status-label">参考图输入：</span>
            <span className={`GoogleBananaNode-status-value ${images.length > 0 ? 'GoogleBananaNode-status-connected' : 'GoogleBananaNode-status-disconnected'}`}>
              {images.length > 0 ? `✓ 已连接 ${images.length} 张` : '✗ 未连接'}
            </span>
          </div>
        </div>

        {/* 宽高比 */}
        <div className="GoogleBananaNode-field">
          <label className="GoogleBananaNode-label">宽高比</label>
          <select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            disabled={isGenerating || readOnly}
            className="GoogleBananaNode-select"
          >
            <option value="1:1">1:1 正方形</option>
            <option value="16:9">16:9 横屏</option>
            <option value="9:16">9:16 竖屏</option>
            <option value="4:3">4:3 标准横屏</option>
            <option value="3:4">3:4 标准竖屏</option>
            {isImageToImageMode && (
              <option value="match_input_image">跟随主图宽高比</option>
            )}
          </select>
        </div>

        {/* 输出格式 */}
        <div className="GoogleBananaNode-field">
          <label className="GoogleBananaNode-label">输出格式</label>
          <div className="GoogleBananaNode-button-group">
            <button
              onClick={() => setOutputFormat('png')}
              disabled={isGenerating || readOnly}
              className={`GoogleBananaNode-button ${outputFormat === 'png' ? 'GoogleBananaNode-button-active' : ''}`}
            >
              PNG
            </button>
            <button
              onClick={() => setOutputFormat('jpg')}
              disabled={isGenerating || readOnly}
              className={`GoogleBananaNode-button ${outputFormat === 'jpg' ? 'GoogleBananaNode-button-active' : ''}`}
            >
              JPG
            </button>
            <button
              onClick={() => setOutputFormat('webp')}
              disabled={isGenerating || readOnly}
              className={`GoogleBananaNode-button ${outputFormat === 'webp' ? 'GoogleBananaNode-button-active' : ''}`}
            >
              WebP
            </button>
          </div>
        </div>

        {/* 提示信息 */}
        <div className="GoogleBananaNode-info">
          <div className="GoogleBananaNode-info-item">
            ⚡ 极速生成：5-15 秒完成
          </div>
          {isImageToImageMode && (
            <div className="GoogleBananaNode-info-item">
              🖼️ 图生图模式：{images.length}/10 张参考图
            </div>
          )}
        </div>

        {/* 使用说明 */}
        <div className="GoogleBananaNode-hint">
          💡 <strong>文生图模式：</strong>仅连接文本输入节点<br />
          🎨 <strong>图生图模式：</strong>连接文本输入 + 图片参考节点（1-10张）<br />
          模式会根据是否连接图片自动切换<br />
          点击生成后会自动创建任务节点
        </div>

        {/* 生成按钮 */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating || readOnly}
          className={`GoogleBananaNode-generate ${
            isGenerating || readOnly ? 'GoogleBananaNode-generate-disabled' : ''
          }`}
        >
          {isGenerating ? '创建中...' : '创建任务'}
        </button>

        {/* 错误提示 */}
        {error && (
          <div className="GoogleBananaNode-error">
            ❌ {error}
          </div>
        )}
      </div>
    </div>
  );
});

GoogleBananaNode.displayName = 'GoogleBananaNode';

